// goddessBarChart.js

// 女神数据配置
const goddessConfig = {
  Demeter: {
    name: "Demeter",
    color: "#e4b4a8",
    chakra: "Root Chakra"
  },
  Aphrodite: {
    name: "Aphrodite",
    color: "#6327a1",
    chakra: "Sacral Chakra"
  },
  Artemis: {
    name: "Artemis",
    color: "#70bf3f",
    chakra: "Solar Plexus"
  },
  Hestia: {
    name: "Hestia",
    color: "#e73861",
    chakra: "Heart Chakra"
  },
  Athena: {
    name: "Athena",
    color: "#d0d0d0",
    chakra: "Throat Chakra"
  },
  Persephone: {
    name: "Persephone",
    color: "#f0df78",
    chakra: "Third Eye"
  },
  Hera: {
    name: "Hera",
    color: "#6b1e18",
    chakra: "Crown Chakra"
  }
};

/**
 * 计算活跃度百分比（旧逻辑，已弃用）
 * @param {number} score - 女神总分 (4-20)
 * @returns {number} 活跃度百分比 (-100 到 100)
 */
function calculateActivityPercent(score) {
  return ((score - 12) / 8) * 100;
}

/**
 * 根据平衡状态映射到柱状图位置
 * @param {string} balance - 'balanced' | 'mild' | 'severe'
 * @returns {number} 柱状图位置百分比 (-100 到 100)
 */
function mapBalanceToPosition(balance) {
  switch (balance) {
    case 'balanced':
      return -65; // -100 到 -33 区间中心
    case 'mild':
      return 0;   // -33 到 +33 区间中心
    case 'severe':
      return 65;  // +33 到 +100 区间中心
    default:
      return 0;   // 默认为 mild
  }
}

/**
 * 创建渐变色
 */
function createGradient(ctx, chartArea, baseColor, isNegative = false) {
  if (!chartArea) return baseColor;

  const gradient = ctx.createLinearGradient(
    0,
    chartArea.bottom,
    0,
    chartArea.top
  );

  // 解析基础颜色
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // 所有柱形从底部开始，渐变从下到上（从深到浅）
  gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.9)`);
  gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.6)`);
  gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0.3)`);

  return gradient;
}

/**
 * 渲染女神柱状图
 * @param {string} elementId - Canvas 元素 ID
 * @param {Object} scores - 女神分数对象 { goddess: score }
 * @param {Object} balanceStates - 女神平衡状态对象 { goddess: 'balanced'|'mild'|'severe' }
 */
async function renderGoddessBarChart(elementId, scores, balanceStates = {}) {
  const ctx = document.getElementById(elementId).getContext('2d');

  // 准备数据
  const labels = Object.keys(scores);
  const activityData = labels.map(goddess => {
    // 如果有平衡状态信息，使用平衡状态映射；否则使用旧的分数计算方法
    const goddessKey = goddess.toLowerCase();
    if (balanceStates[goddessKey]) {
      return mapBalanceToPosition(balanceStates[goddessKey]);
    } else {
      const score = scores[goddess];
      return calculateActivityPercent(score);
    }
  });

  const colors = labels.map(goddess => goddessConfig[goddess].color);
  const chineseLabels = labels.map(goddess => goddessConfig[goddess].name);

  // 响应式字体大小
  function getResponsiveFontSize() {
    const width = window.innerWidth;
    return width < 768 ? 9 : 14;
  }

  // 响应式左侧padding
  function getResponsiveLeftPadding() {
    const width = window.innerWidth;
    return width < 768 ? 80 : 130;
  }

  // 响应式标签间距
  function getResponsiveLabelSpacing() {
    const width = window.innerWidth;
    return width < 768 ? 10 : 12;
  }

  const fontSize = getResponsiveFontSize();
  const leftPadding = getResponsiveLeftPadding();
  const labelSpacing = getResponsiveLabelSpacing();

  // 背景区域插件
  const backgroundZonesPlugin = {
    id: 'backgroundZones',
    beforeDraw: (chart) => {
      const {ctx, chartArea, scales, canvas} = chart;
      if (!chartArea) return;

      const yScale = scales.y;
      // 背景区域延伸到图表外（左侧），但不延伸到右侧
      const xLeft = 0;
      const xRight = chartArea.right;

      // 保存状态
      ctx.save();

      // 区域1: -100% 至 -33% 灰色（不活跃）
      const zone1Top = yScale.getPixelForValue(-33);
      const zone1Bottom = yScale.getPixelForValue(-100);
      ctx.fillStyle = 'rgba(220, 220, 220, 0.3)';
      ctx.fillRect(xLeft, zone1Top, xRight - xLeft, zone1Bottom - zone1Top);

      // 区域2: -33% 至 +33% 橙色（适度活跃）
      const zone2Top = yScale.getPixelForValue(33);
      const zone2Bottom = yScale.getPixelForValue(-33);
      ctx.fillStyle = 'rgba(255, 235, 215, 0.4)';
      ctx.fillRect(xLeft, zone2Top, xRight - xLeft, zone2Bottom - zone2Top);

      // 区域3: +33% 至 +100% 粉色（过度活跃）
      const zone3Top = yScale.getPixelForValue(100);
      const zone3Bottom = yScale.getPixelForValue(33);
      ctx.fillStyle = 'rgba(255, 220, 220, 0.3)';
      ctx.fillRect(xLeft, zone3Top, xRight - xLeft, zone3Bottom - zone3Top);

      // 在图表外左侧添加标签
      // 动态获取响应式字体大小和间距
      const currentFontSize = getResponsiveFontSize();
      const currentLabelSpacing = getResponsiveLabelSpacing();
      ctx.font = `${currentFontSize}px Georgia, "Noto Serif SC", serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // 辅助函数：绘制圆角矩形
      const drawRoundedRect = (x, y, width, height, radius) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
      };

      // 标签配置
      const labels = [
        { text: 'Balanced', y: (zone1Top + zone1Bottom) / 2 },
        { text: 'Mild Imbalance', y: (zone2Top + zone2Bottom) / 2 },
        { text: 'Severe Imbalance', y: (zone3Top + zone3Bottom) / 2 }
      ];

      // 绘制每个标签的背景和文字
      labels.forEach(label => {
        // 测量文字宽度和高度
        const textMetrics = ctx.measureText(label.text);
        const textWidth = textMetrics.width;
        // 使用字体的实际高度（包括 ascent 和 descent）
        const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent || currentFontSize;

        // 背景参数（移动端使用更小的padding）
        const padding = window.innerWidth < 768 ? 4 : 6;
        const bgWidth = textWidth + padding * 2;
        const bgHeight = textHeight + padding * 2;
        // 标签位置：在 chartArea 左侧，使用响应式间距
        const bgX = chartArea.left - currentLabelSpacing - bgWidth;
        // 确保背景矩形在区域中垂直居中
        const bgY = label.y - bgHeight / 2;
        const radius = 4;

        // 计算文字居中位置（水平和垂直都居中）
        const textX = bgX + bgWidth / 2;
        // 文字垂直居中：背景中心位置
        const textY = bgY + bgHeight / 2;

        // 绘制白色圆角矩形背景
        ctx.fillStyle = 'white';
        drawRoundedRect(bgX, bgY, bgWidth, bgHeight, radius);
        ctx.fill();

        // 绘制文字（水平和垂直都居中）
        ctx.fillStyle = 'rgba(139, 111, 71, 0.7)';
        ctx.fillText(label.text, textX, textY);
      });

      // 恢复状态
      ctx.restore();
    }
  };

  // 创建图表
  const chart = new Chart(ctx, {
    type: 'bar',
    plugins: [backgroundZonesPlugin],
    data: {
      labels: chineseLabels,
      datasets: [{
        label: '活跃度 %',
        data: activityData,
        base: -100,
        backgroundColor: function(context) {
          const chart = context.chart;
          const {ctx, chartArea} = chart;

          if (!context.parsed) return 'rgba(200, 200, 200, 0.5)';

          const value = context.parsed.y;
          const goddess = labels[context.dataIndex];
          const baseColor = goddessConfig[goddess].color;

          // 所有柱形从底部开始，使用从下到上的渐变
          return createGradient(ctx, chartArea, baseColor, false);
        },
        borderWidth: 0,
        borderRadius: 8,
        borderSkipped: false,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: false
      },
      onHover: null,
      layout: {
        padding: {
          top: 20,
          bottom: 10,
          left: leftPadding,
          right: 20
        }
      },
      datasets: {
        bar: {
          barPercentage: 0.5,
          categoryPercentage: 0.8
        }
      },
      scales: {
        y: {
          min: -100,
          max: 100,
          ticks: {
            display: false
          },
          grid: {
            display: false
          },
          border: {
            display: false
          }
        },
        x: {
          ticks: {
            font: {
              size: fontSize + 1,
              family: 'Georgia, "Noto Serif SC", serif',
              weight: '500'
            },
            color: '#6b5742'
          },
          grid: {
            display: false
          },
          border: {
            color: 'rgba(139, 111, 71, 0.3)'
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false
        },
        datalabels: {
          display: false
        }
      },
      animation: {
        duration: 1500,
        easing: 'easeInOutQuart'
      }
    }
  });

  // 响应式更新
  window.addEventListener('resize', () => {
    const newFontSize = getResponsiveFontSize();
    const newLeftPadding = getResponsiveLeftPadding();
    chart.options.scales.x.ticks.font.size = newFontSize + 1;
    chart.options.plugins.tooltip.titleFont.size = newFontSize + 2;
    chart.options.plugins.tooltip.bodyFont.size = newFontSize;
    chart.options.layout.padding.left = newLeftPadding;
    chart.update();
  });

  return chart;
}
